import React, { useState, useEffect } from 'react';
import { scheduleApi, employeeApi } from '../../lib/api';
import { departmentsApi, Department } from '../../lib/api/departments.api';
import Button from '../../components/common/Button';
// import Input from '../../components/common/Input';
import Loader from '../../components/common/Loader';
import ScheduleForm from '../../components/backoffice/ScheduleForm';
import WeeklyCalendarSimple from '../../components/backoffice/WeeklyCalendarSimple';
import { Employee } from '../../types/weeklySchedule';

interface Schedule {
    id: string;
    name: string;
    daysOfWeek?: number[];
    dayOfWeek?: number; // Para compatibilidad con datos antiguos
    startTime: string;
    endTime: string;
    color?: string;
    createdAt: string;
    updatedAt: string;
    // Periodo de gracia
    graceEnabled?: boolean;
    graceInBeforeStartMin?: number;
    graceInNoLateAfterStartMin?: number;
    graceInAfterStartMaxMin?: number;
    graceOutBeforeEndMin?: number;
    graceOutAfterEndMin?: number;
}

const HorariosPage: React.FC = () => {
    const [schedules, setSchedules] = useState<Schedule[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [activeTab, setActiveTab] = useState<'list' | 'calendar'>('list');
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [filterDepartment, setFilterDepartment] = useState<string>('');
    const [filterPosition, setFilterPosition] = useState<string>('');

    // Obtener posiciones únicas de los empleados
    const positions = React.useMemo(() => {
        const uniquePositions = new Set<string>();
        employees.forEach(emp => {
            if (emp.position && emp.position.trim()) {
                uniquePositions.add(emp.position);
            }
        });
        return Array.from(uniquePositions).sort();
    }, [employees]);

    useEffect(() => {
        loadSchedules();
        loadEmployees();
        loadDepartments();
    }, []);

    const loadSchedules = async () => {
        try {
            setLoading(true);
            const response = await scheduleApi.getSchedules();
            if (response.success && response.data) {
                setSchedules(response.data.schedules || []);
            }
        } catch (error: any) {
            setError(error.message || 'Error al cargar los horarios');
        } finally {
            setLoading(false);
        }
    };

    const loadEmployees = async () => {
        try {
            const response = await employeeApi.getEmployees({ active: true });
            if (response.success && response.data) {
                setEmployees(response.data.employees || []);
            }
        } catch (error: any) {
            console.error('Error al cargar empleados:', error);
        }
    };

    const loadDepartments = async () => {
        try {
            const response = await departmentsApi.getDepartments();
            setDepartments(response);
        } catch (error: any) {
            console.error('Error al cargar departamentos:', error);
        }
    };

    const handleCreateSchedule = () => {
        setEditingSchedule(null);
        setShowForm(true);
    };

    const handleEditSchedule = (schedule: Schedule) => {
        setEditingSchedule(schedule);
        setShowForm(true);
    };

    const handleDeleteSchedule = async (schedule: Schedule) => {
        const confirmMessage = `¿Estás seguro de que deseas eliminar el horario "${schedule.name}"?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            await scheduleApi.deleteSchedule(schedule.id);
            await loadSchedules();
        } catch (error: any) {
            // Si hay empleados asignados, preguntar si desea forzar la eliminación
            if (error.message && error.message.includes('empleados asignados')) {
                const forceConfirm = window.confirm(
                    `${error.message}\n\n¿Deseas forzar la eliminación? Esto también eliminará las asignaciones de empleados a este horario.`
                );

                if (forceConfirm) {
                    try {
                        await scheduleApi.deleteSchedule(schedule.id, true);
                        await loadSchedules();
                    } catch (forceError: any) {
                        setError(forceError.message || 'Error al eliminar el horario');
                    }
                }
            } else {
                setError(error.message || 'Error al eliminar el horario');
            }
        }
    };

    const handleFormSuccess = () => {
        setShowForm(false);
        setEditingSchedule(null);
        loadSchedules();
    };

    const handleFormCancel = () => {
        setShowForm(false);
        setEditingSchedule(null);
    };

    const formatTime = (timeString: string) => {
        return timeString.substring(0, 5);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const filteredSchedules = [...schedules]
        .sort((a, b) => a.startTime.localeCompare(b.startTime))
        .filter(schedule => {
            if (!searchTerm.trim()) return true;

            const term = searchTerm.toLowerCase();
            const nameMatch = schedule.name.toLowerCase().includes(term);
            const timeMatch = `${formatTime(schedule.startTime)} - ${formatTime(schedule.endTime)}`.includes(term);
            
            return nameMatch || timeMatch;
        });

    if (showForm) {
        return (
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {editingSchedule ? 'Editar Horario' : 'Nuevo Horario'}
                    </h1>
                    <Button
                        onClick={handleFormCancel}
                        variant="secondary"
                    >
                        Cancelar
                    </Button>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <ScheduleForm
                        schedule={editingSchedule ? {
                            id: editingSchedule.id,
                            name: editingSchedule.name,
                            startTime: editingSchedule.startTime,
                            endTime: editingSchedule.endTime,
                            color: editingSchedule.color,
                            // Periodo de gracia
                            graceEnabled: editingSchedule.graceEnabled,
                            graceInBeforeStartMin: editingSchedule.graceInBeforeStartMin,
                            graceInNoLateAfterStartMin: editingSchedule.graceInNoLateAfterStartMin,
                            graceInAfterStartMaxMin: editingSchedule.graceInAfterStartMaxMin,
                            graceOutBeforeEndMin: editingSchedule.graceOutBeforeEndMin,
                            graceOutAfterEndMin: editingSchedule.graceOutAfterEndMin,
                        } : undefined}
                        onSuccess={handleFormSuccess}
                        onCancel={handleFormCancel}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gestión de Horarios</h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Administra los horarios y turnos de trabajo de tu empresa.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={handleCreateSchedule}
                        variant={activeTab === 'list' ? 'primary' : 'outline'}
                    >
                        Nuevo Horario
                    </Button>
                </div>
            </div>

            {/* Tabs de navegación */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('list')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'list'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:border-gray-600'
                            }`}
                    >
                        Lista de Horarios
                    </button>
                    <button
                        onClick={() => setActiveTab('calendar')}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'calendar'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:border-gray-600'
                            }`}
                    >
                        Calendario Semanal
                    </button>
                </nav>
            </div>

            {/* Contenido según tab activo */}
            {activeTab === 'calendar' ? (
                <>
                    <div className="mb-6">
                        <div className="flex items-center space-x-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Filtrar por departamento
                                </label>
                                <select
                                    value={filterDepartment}
                                    onChange={(e) => setFilterDepartment(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                                >
                                    <option value="">Todos los departamentos</option>
                                    {departments.map(department => (
                                        <option key={department.id} value={department.id}>
                                            {department.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Filtrar por posición
                                </label>
                                <select
                                    value={filterPosition}
                                    onChange={(e) => setFilterPosition(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                                >
                                    <option value="">Todas las posiciones</option>
                                    {positions.map(position => (
                                        <option key={position} value={position}>
                                            {position}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    <WeeklyCalendarSimple
                        employees={employees}
                        filterDepartment={filterDepartment}
                        filterPosition={filterPosition}
                    />
                </>
            ) : (
                <>
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                            {error}
                        </div>
                    )}

                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                        <div className="mb-6">
                            <div className="flex items-center space-x-4">
                                <div className="flex-1 max-w-md">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Buscar horario
                                    </label>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Buscar por nombre o horario..."
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 dark:bg-gray-700 dark:text-white"
                                    />
                                </div>
                                <Button
                                    onClick={loadSchedules}
                                    variant="secondary"
                                >
                                    Actualizar
                                </Button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="flex justify-center py-8">
                                <Loader size="lg" />
                            </div>
                        ) : filteredSchedules.length === 0 ? (
                            <div className="text-center py-8">
                                <div className="text-gray-400 text-lg mb-2">🕐</div>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                    {!searchTerm.trim() ? 'No hay horarios configurados' : 'No se encontraron horarios'}
                                </h3>
                                <p className="text-gray-500 dark:text-gray-400 mb-4">
                                    {!searchTerm.trim()
                                        ? 'Crea tu primer horario para comenzar'
                                        : 'Intenta con otro término de búsqueda'}
                                </p>
                                {!searchTerm.trim() && (
                                    <Button onClick={handleCreateSchedule}>
                                        Crear Primer Horario
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50 dark:bg-gray-800">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Nombre
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Horario
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Creado
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                Acciones
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {filteredSchedules.map((schedule) => (
                                            <tr key={schedule.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div
                                                            className="w-4 h-4 rounded mr-3 flex-shrink-0"
                                                            style={{
                                                                backgroundColor: schedule.color || '#3B82F6',
                                                                border: '1px solid rgba(0,0,0,0.1)'
                                                            }}
                                                        />
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {schedule.name}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                                    {formatDate(schedule.createdAt)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex justify-end space-x-2">
                                                        <Button
                                                            onClick={() => handleEditSchedule(schedule)}
                                                            variant="secondary"
                                                            size="sm"
                                                        >
                                                            Editar
                                                        </Button>
                                                        <Button
                                                            onClick={() => handleDeleteSchedule(schedule)}
                                                            variant="danger"
                                                            size="sm"
                                                        >
                                                            Eliminar
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default HorariosPage;